import { z } from "zod/v4";
import { absentable, moneySchema, vatRateSchema } from "./catalog";
import { campIdSchema, editionIdSchema, instantSchema, localDateSchema } from "./ids";

/** `completed` is derived by the API once `endsOn` has passed. */
export const editionStatusSchema = z.enum(["draft", "open", "closed", "cancelled", "completed"]);
export type EditionStatus = z.infer<typeof editionStatusSchema>;

export const MAX_CAPACITY = 500;
/** Highest price per person the portal sends: 100,000 euro, far inside the API's i32 cents. */
export const MAX_PRICE_MINOR = 10_000_000;
/** The API's limits on the free-text terms of an edition. */
export const CANCELLATION_TERMS_MAX_LENGTH = 8000;
export const TERMS_MAX_LENGTH = 20000;

export const minimumToRunSchema = z.object({
	decideBy: localDateSchema,
	headcount: z.number().int().min(1).max(MAX_CAPACITY),
});
export type MinimumToRun = z.infer<typeof minimumToRunSchema>;

export const editionSchema = z.object({
	campId: campIdSchema,
	cancellationTerms: z.string(),
	capacity: z.number().int().positive(),
	endsOn: localDateSchema,
	id: editionIdSchema,
	minimumToRun: absentable(minimumToRunSchema),
	price: moneySchema,
	registrationClosesAt: absentable(instantSchema),
	startsOn: localDateSchema,
	status: editionStatusSchema,
	taken: z.number().int().nonnegative(),
	terms: z.string(),
	timezone: z.string(),
	vatRate: vatRateSchema,
	waitlistEnabled: z.boolean(),
});
export type Edition = z.infer<typeof editionSchema>;

/** Cancelling answers with the count, not the edition: the portal refetches the edition itself. */
export const editionCancelResultSchema = z.object({
	cancelledRegistrations: z.number().int().nonnegative(),
	id: editionIdSchema,
	status: z.literal("cancelled"),
});
export type EditionCancelResult = z.infer<typeof editionCancelResultSchema>;

export const editionBodySchema = z
	.object({
		cancellationTerms: z.string().trim().min(1).max(CANCELLATION_TERMS_MAX_LENGTH),
		capacity: z.number().int().min(1).max(MAX_CAPACITY),
		endsOn: localDateSchema,
		minimumToRun: minimumToRunSchema.optional(),
		price: moneySchema.extend({ amountMinor: z.number().int().min(0).max(MAX_PRICE_MINOR) }),
		registrationClosesAt: instantSchema.optional(),
		startsOn: localDateSchema,
		terms: z.string().trim().max(TERMS_MAX_LENGTH),
		timezone: z.string().min(1),
		vatRate: vatRateSchema,
		waitlistEnabled: z.boolean(),
	})
	.refine((body) => body.endsOn >= body.startsOn, {
		message: "endsOn must not be before startsOn",
		path: ["endsOn"],
	});
export type EditionBody = z.infer<typeof editionBodySchema>;
